/// <amd-module name="TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Stores/AboutProjectStore" />
import { Store } from "VSS/Reflux/Store";

import { AboutProjectData, AboutRegionInfo, AllProjectTagsInCollectionData, TagsData } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Models";
import { StoreLoadingState, UpdatingState } from "TFS/Views/ProjectOverviewPage/Shared/Contracts";

export interface AboutProjectState {
    loadingState: StoreLoadingState;
    aboutProjectData: AboutProjectData | undefined;
    readmeSettingUpdateState: UpdatingState;
    projectTagsUpdateState: UpdatingState;
    allProjectTagsInCollectionData: AllProjectTagsInCollectionData;
}

export namespace AboutProjectStoreEvents {
    export const UpdateEvent = "UpdateEvent";
}

export class AboutProjectStore extends Store {
    private _state: AboutProjectState = {
        loadingState: StoreLoadingState.Loading,
        aboutProjectData: undefined,
        readmeSettingUpdateState: UpdatingState.NotUpdating,
        projectTagsUpdateState: UpdatingState.NotUpdating,
        allProjectTagsInCollectionData: { tags: [], tagsFetched: false }
    };

    public getState(): AboutProjectState {
        return this._state;
    }

    public loadAboutProject = (payload: AboutProjectData): void => {
        this._state.loadingState = StoreLoadingState.Loaded;
        this._state.aboutProjectData = payload;
        this.emitChanged();
    };

    public loadAboutProjectFailed = (): void => {
        this._state.loadingState = StoreLoadingState.LoadFailed;
        this.emitChanged();
    };

    public readmeSettingUpdated = (aboutInfo: AboutRegionInfo): void => {
        const aboutProjectData = this._state.aboutProjectData;
        if (
            aboutProjectData &&
            (aboutProjectData.repositoryId !== aboutInfo.repositoryId || aboutProjectData.sourceRepositoryType !== aboutInfo.repositoryType)
        ) {
            this._state.aboutProjectData = {
                ...aboutProjectData,
                repositoryId: aboutInfo.repositoryId,
                sourceRepositoryType: aboutInfo.repositoryType
            };
            this._updateReadmeSettingState(UpdatingState.Succeeded);
        }
    };

    public readmeSettingUpdateFailed = (): void => {
        this._updateReadmeSettingState(UpdatingState.Failed);
    };

    public readmeSettingUpdateStarted = (): void => {
        this._updateReadmeSettingState(UpdatingState.InProgress);
    };

    public readmeSettingNotUpdating = (): void => {
        this._updateReadmeSettingState(UpdatingState.NotUpdating);
    };

    public projectTagsUpdated = (tags: string[]): void => {
        const aboutProjectData = this._state.aboutProjectData;

        if (aboutProjectData) {
            this._state.aboutProjectData = {
                ...aboutProjectData,
                tagsData: { tags }
            };
            this._updateProjectTagsState(UpdatingState.Succeeded);
        }
    };

    public projectTagsUpdateFailed = (): void => {
        this._updateProjectTagsState(UpdatingState.Failed);
    };

    public projectTagsUpdateStarted = (): void => {
        this._updateProjectTagsState(UpdatingState.InProgress);
    };

    public projectTagsNotUpdating = (): void => {
        this._updateProjectTagsState(UpdatingState.NotUpdating);
    };

    public loadAllProjectTagsInCollection = (tags: string[]): void => {
        this._state.allProjectTagsInCollectionData = {
            tagsFetched: true,
            tags: tags
        };
        this.emitChanged();
    };

    private _updateReadmeSettingState(updatingState: UpdatingState): void {
        this._state.readmeSettingUpdateState = updatingState;
        this.emit(AboutProjectStoreEvents.UpdateEvent, this);
    }

    private _updateProjectTagsState(updatingState: UpdatingState): void {
        this._state.projectTagsUpdateState = updatingState;
        this.emit(AboutProjectStoreEvents.UpdateEvent, this);
    }
}
